import { useEffect, useState } from "react";
import { X, Share, Plus } from "lucide-react";

const DISMISS_KEY = "checkmate-install-dismissed";
// Note: localStorage here stores ONLY a dismissal expiry timestamp (30-day
// cooldown so we don't nag users). No PII, tokens, or credentials touch
// this key — safe on the client per Feb 2026 code-review pass.

/**
 * Mobile install prompt.
 * - Android/Chrome: listens for `beforeinstallprompt` and shows a native install button.
 * - iOS Safari: no programmatic install API, so we render an inline instruction card.
 * Dismissal is persisted for 30 days.
 */
// Stores ONE non-sensitive UI preference in localStorage: a "snooze until"
// epoch so a dismissed install prompt stays dismissed for 30 days. No personal
// data, tokens or session material is ever written here.
export default function InstallPrompt() {
  const [deferredEvent, setDeferredEvent] = useState(null);
  const [isIos, setIsIos] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (raw) {
      const until = parseInt(raw, 10);
      if (!Number.isNaN(until) && Date.now() < until) return;
    }
    const ua = window.navigator.userAgent || "";
    const isIosDevice = /iPhone|iPad|iPod/i.test(ua) && !window.MSStream;
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
    const isMobile = /Android|iPhone|iPad|iPod/i.test(ua) || window.innerWidth < 640;
    if (isStandalone || !isMobile) return;

    setIsIos(isIosDevice);
    if (isIosDevice) {
      setVisible(true);
      return;
    }
    const handler = (e) => {
      e.preventDefault();
      setDeferredEvent(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 30 * 24 * 60 * 60 * 1000));
    setVisible(false);
  };

  const install = async () => {
    if (!deferredEvent) return;
    deferredEvent.prompt();
    const { outcome } = await deferredEvent.userChoice;
    if (outcome === "accepted" || outcome === "dismissed") {
      setVisible(false);
      if (outcome === "dismissed") dismiss();
    }
  };

  if (!visible) return null;

  return (
    <div data-testid="install-prompt" className="fixed bottom-4 left-4 right-4 z-50 sm:left-auto sm:right-4 sm:w-80 bg-ink text-white border-2 border-ink shadow-lg p-4">
      <button
        data-testid="install-dismiss"
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute top-2 right-2 text-white/60 hover:text-white"
      ><X className="w-4 h-4" /></button>
      <div className="overline text-warn mb-1">§ MOBILE SHORTCUT</div>
      <div className="font-heading font-black text-lg mb-2 leading-tight">Save Checkmate to Home Screen</div>
      {isIos ? (
        <div className="font-mono text-xs text-white/80 space-y-1">
          <p>Tap the <Share className="w-3 h-3 inline mx-0.5" /> Share button in Safari, then choose <b>Add to Home Screen</b>.</p>
          <p className="text-white/60">You'll get one-tap access to Clock In and Pay Stubs.</p>
        </div>
      ) : (
        <>
          <p className="font-mono text-xs text-white/80 mb-3">Add Checkmate Payroll to your phone's home screen for one-tap access to Clock In and Pay Stubs.</p>
          <button
            data-testid="install-accept"
            onClick={install}
            className="w-full bg-warn text-ink px-4 py-2 font-mono uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2 hover:bg-warn/90"
          ><Plus className="w-3.5 h-3.5" /> Install App</button>
        </>
      )}
    </div>
  );
}
